import React, { useState, useEffect, useMemo, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  LogOut,
  User,
  BarChart,
  BookOpen,
  Settings,
  UserCog,
  Activity,
  MapPin,
  Users,
  MessageCircle,
  Mail,
  Heart,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Bot,
} from "lucide-react";

const FilledFolderIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M2 6C2 4.89543 2.89543 4 4 4H9.17157C9.70201 4 10.2107 4.21071 10.5858 4.58579L12 6H20C21.1046 6 22 6.89543 22 8V18C22 19.1046 21.1046 20 20 20H4C2.89543 20 2 19.1046 2 18V6Z" />
  </svg>
);
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
  SidebarHeader,
} from "@/components/ui/sidebar";
import { MemberManager } from "@/components/admin/MemberManager";
import { ActivityManager } from "@/components/admin/ActivityManager";
import { ActivityFeed } from "@/components/admin/ActivityFeed";
import { CustomDashboard } from "@/components/admin/CustomDashboard";
import PerfMetricsPanel from "@/components/admin/PerfMetricsPanel";
import { EnhancedRegionalDashboard } from "@/components/admin/EnhancedRegionalDashboard";
import { SettingsManager } from "@/components/admin/SettingsManager";
import { AuditLogViewer } from "@/components/admin/AuditLogViewer";
import { KitDashboard } from "@/components/admin/KitDashboard";
import { GalleryManager } from "@/components/admin/GalleryManager";
import { ChatSessionsManager } from "@/components/admin/ChatSessionsManager";
import { MessagesManager } from "@/components/admin/MessagesManager";
import { PrayerRequestsManager } from "@/components/admin/PrayerRequestsManager";
import { ConferenceRegistrationsManager } from "@/components/admin/ConferenceRegistrationsManager";
import BadgeGenerator from "@/components/admin/BadgeGenerator";
import { PaymentConfirmationsPanel } from "@/components/admin/PaymentConfirmationsPanel";
import { ScheduledReportsManager } from "@/components/admin/ScheduledReportsManager";
import LiveSheetSyncPanel from "@/components/admin/LiveSheetSyncPanel";
import ConferenceAnalyticsDashboard from "@/components/admin/ConferenceAnalyticsDashboard";
import CheckinDashboard from "@/components/admin/CheckinDashboard";
import CancellationRequestsManager from "@/components/admin/CancellationRequestsManager";
import { ConferenceContentManager } from "@/components/admin/ConferenceContentManager";
import { GallerySettingsManager } from "@/components/admin/GallerySettingsManager";
import ConferenceKitDashboard from "@/components/admin/ConferenceKitDashboard";
import NoticesManager from "@/components/admin/NoticesManager";
import AdminNoticesBanner from "@/components/admin/AdminNoticesBanner";
import PrayerLetterManager from "@/components/admin/PrayerLetterManager";
import MCPConsole from "@/components/admin/mcp/MCPConsole";
import { useAuth } from "@/components/auth/AuthProvider";
import { toast } from "@/hooks/use-toast";
import { useLocation, useNavigate } from "react-router-dom";
import { getAdminTabPath, getSafeAdminReturnPath, rememberAdminReturnPath } from "@/lib/adminRoutePersistence";

const AdminSidebar = ({
  activeTab,
  setActiveTab,
  navItems,
}: {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  navItems: any[];
}) => {
  const { open, isMobile, setOpenMobile } = useSidebar();

  return (
    <Sidebar className="border-r" collapsible="icon" variant="sidebar">
      <SidebarHeader className="border-b p-4">
        <div className="flex items-center justify-between">
          {(open || isMobile) && <h2 className="font-semibold text-lg">Menu</h2>}
          <SidebarTrigger className={open || isMobile ? "" : "mx-auto"}>
            {open ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </SidebarTrigger>
        </div>
      </SidebarHeader>
      <SidebarContent className="pt-2">
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <SidebarMenuItem key={item.id}>
                    <SidebarMenuButton
                      onClick={() => {
                        setActiveTab(item.id);
                        if (isMobile) setOpenMobile(false);
                      }}
                      isActive={activeTab === item.id}
                      tooltip={item.label}
                      className={`py-3 ${activeTab === item.id ? "bg-sidebar-accent text-sidebar-accent-foreground font-semibold border-l-4 border-primary" : ""}`}
                    >
                      <Icon className="h-5 w-5" />
                      <span className="text-base flex items-center gap-2">
                        {item.label}
                        {item.beta && (
                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                            BETA
                          </Badge>
                        )}
                      </span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
};

const AdminDashboard = () => {
  const [activeTab, setActiveTabState] = useState("dashboard");
  const [registrationRegion, setRegistrationRegion] = useState("all");
  const { adminUser, signOut, isGlobalAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Parse tab segment from /admin/dashboard/<tab> so this component stays
  // mounted across tab changes (single splat route in App.tsx).
  const routeTab = location.pathname.split("/")[3] || undefined;
  // Stable per-mount id used by e2e tests to verify the dashboard stays
  // mounted (i.e. does not flicker/remount) across tab navigation.
  const mountIdRef = React.useRef<string>(
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2),
  );

  // Track whether we've ever been signed in as an admin this session.
  // Prevents transient loading states (token refresh, tab focus) from
  // redirecting to /admin and wiping in-page state.
  const wasAdminRef = React.useRef(false);
  if (adminUser) wasAdminRef.current = true;

  // Redirect if not authenticated (only on the initial check).
  useEffect(() => {
    if (!loading && !adminUser && !wasAdminRef.current) {
      rememberAdminReturnPath(`${location.pathname}${location.search}`);
      navigate("/admin", { replace: true, state: { from: location } });
    }
  }, [loading, adminUser, navigate, location]);

  // Define tab visibility based on role
  const visibleTabs = {
    global_admin: [
      "dashboard", "members", "activities", "regions", "kit", "messages",
      "gallery", "gallery-settings", "chats", "prayers", "prayer-letter", "registrations", "badges", "conf-analytics", "checkin", "cancellations",
      "conference-content", "conference-kit", "notices", "audit", "mcp-console", "settings",
    ],
    kit_admin: ["dashboard", "kit", "notices"],
    prayer_secretary: ["dashboard", "prayer-letter"],
    president: [
      "dashboard", "members", "activities", "regions", "kit", "messages",
      "gallery", "gallery-settings", "chats", "prayers", "prayer-letter", "registrations", "badges", "conf-analytics", "checkin", "cancellations",
      "conference-content", "conference-kit", "notices", "mcp-console", "settings",
    ],
    regional_admin: ["dashboard", "members", "activities", "regions", "kit", "messages", "gallery", "chats", "prayers", "registrations", "badges", "conf-analytics", "checkin", "cancellations", "conference-content", "conference-kit", "notices", "mcp-console"],
  };

  const userVisibleTabs = useMemo(
    () => visibleTabs[adminUser?.role as keyof typeof visibleTabs] || ["dashboard"],
    [adminUser?.role],
  );

  const setActiveTab = useCallback((tab: string) => {
    const nextPath = getAdminTabPath(tab);
    // Preserve current query params when the user is staying on the same tab
    // (e.g. filter/pagination state kept in URL). Reset them when switching tabs.
    const isSameTab = routeTab === tab;
    const nextSearch = isSameTab ? location.search : "";
    setActiveTabState(tab);
    rememberAdminReturnPath(`${nextPath}${nextSearch}`);
    if (`${location.pathname}${location.search}` !== `${nextPath}${nextSearch}`) {
      navigate(`${nextPath}${nextSearch}`, { replace: false });
    }
  }, [location.pathname, location.search, navigate, routeTab]);

  // Ensure activeTab is valid for the current user's role
  useEffect(() => {
    if (!adminUser) return;

    if (!routeTab) {
      const savedPath = getSafeAdminReturnPath();
      const savedTab = new URL(savedPath, window.location.origin).pathname.split("/")[3];
      const target = savedTab && userVisibleTabs.includes(savedTab) ? savedPath : getAdminTabPath("dashboard");
      navigate(target, { replace: true });
      return;
    }

    if (!userVisibleTabs.includes(routeTab)) {
      console.warn("[admin-route] Admin tried to open unavailable tab", { role: adminUser.role, routeTab });
      setActiveTabState("dashboard");
      navigate(getAdminTabPath("dashboard"), { replace: true });
      return;
    }

    if (activeTab !== routeTab) setActiveTabState(routeTab);
    rememberAdminReturnPath(`${location.pathname}${location.search}`);
  }, [adminUser, userVisibleTabs, activeTab, routeTab, location.pathname, location.search, navigate]);

  // Check if user has KIT access (global_admin, president, or kit_admin)
  const hasKitAccess =
    adminUser?.role === "global_admin" || adminUser?.role === "president" || adminUser?.role === "kit_admin";

  const logout = async () => {
    // Clear storage FIRST so any auth listener that fires during signOut
    // can't re-hydrate a session from localStorage.
    try {
      localStorage.removeItem("ofuesi_secure_session");
      Object.keys(localStorage)
        .filter((k) => k.startsWith("sb-") || k.includes("supabase.auth"))
        .forEach((k) => localStorage.removeItem(k));
      sessionStorage.clear();
    } catch (e) {
      console.error("Storage clear error:", e);
    }

    try {
      // Local-only signOut — avoids slow/failing network round-trip that
      // can leave React state mid-flight while the page is replacing.
      await supabase.auth.signOut({ scope: "local" });
    } catch (error) {
      console.error("Supabase signOut error:", error);
    }

    // Hard replace so React state can never re-render the dashboard and
    // the back button can't return to it.
    window.location.replace("/admin");
  };

  // Only block on the initial load — once we've been admin, keep the UI
  // mounted through background token refreshes so filters/state survive.
  if (loading && !wasAdminRef.current) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!adminUser && !wasAdminRef.current) {
    return null;
  }


  const navItems = [
    { id: "dashboard", label: "Dashboard", icon: BarChart, beta: false },
    { id: "members", label: "Members", icon: UserCog, beta: false },
    { id: "activities", label: "Activities", icon: Activity, beta: false },
    { id: "regions", label: "Regions", icon: MapPin, beta: false },
    { id: "kit", label: "KIT", icon: BookOpen, beta: false },
    { id: "messages", label: "Messages", icon: Mail, beta: false },
    { id: "gallery", label: "Gallery", icon: FilledFolderIcon, beta: false },
    { id: "gallery-settings", label: "Gallery Link & Analytics", icon: BarChart, beta: false },
    { id: "chats", label: "Chat Sessions", icon: MessageCircle, beta: false },
    { id: "prayers", label: "Prayer Requests", icon: Heart, beta: false },
    { id: "prayer-letter", label: "Prayer Letter", icon: Heart, beta: false },
    { id: "registrations", label: "Conference Registrations", icon: ClipboardList, beta: false },
    { id: "badges", label: "Badge Generator", icon: ClipboardList, beta: true },
    { id: "conf-analytics", label: "Conference Analytics", icon: BarChart, beta: false },
    { id: "checkin", label: "Conference Check-in", icon: ClipboardList, beta: true },
    { id: "cancellations", label: "Cancellation Requests", icon: ClipboardList, beta: false },
    { id: "conference-content", label: "Conference Content", icon: ClipboardList, beta: false },
    { id: "conference-kit", label: "Conference Kit", icon: ClipboardList, beta: true },
    { id: "notices", label: "Admin Notices", icon: Mail, beta: false },
    ...(adminUser?.role === "global_admin" ? [{ id: "audit", label: "Audit Logs", icon: Settings, beta: true }] : []),
    { id: "mcp-console", label: "Agent Integrations (MCP)", icon: Bot, beta: true },
    { id: "settings", label: "Settings", icon: Settings, beta: false },
  ].filter((item) => userVisibleTabs.includes(item.id));

  return (
    <SidebarProvider defaultOpen={true} collapsedWidth={56}>
      <div className="flex min-h-screen w-full bg-background" data-testid="admin-dashboard-root" data-mount-id={mountIdRef.current}>
        <AdminSidebar activeTab={activeTab} setActiveTab={setActiveTab} navItems={navItems} />

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header */}
          <header className="border-b bg-card sticky top-0 z-40 pt-[env(safe-area-inset-top)]">
            <div className="px-4 py-3 sm:py-4 flex items-center justify-between gap-2">
              <div className="flex items-center gap-3 min-w-0">
                <SidebarTrigger
                  className="md:hidden h-11 w-11 shrink-0"
                  aria-label="Open admin menu"
                />
                <div className="min-w-0">
                  <h1 className="text-lg sm:text-2xl font-bold truncate">OFUESI Admin</h1>
                  <Badge variant="secondary" className="mt-1 hidden sm:inline-flex">
                    {adminUser?.role === "global_admin"
                      ? "Global Admin"
                      : adminUser?.role === "president"
                        ? "President"
                        : adminUser?.role === "kit_admin"
                          ? "KIT Admin"
                          : adminUser?.role === "prayer_secretary"
                            ? "Prayer Secretary"
                            : "Regional Coordinator"}
                  </Badge>
                </div>
              </div>
              <div className="flex items-center gap-2 sm:gap-4 shrink-0">
                <div className="hidden sm:flex items-center gap-2 max-w-[220px]">
                  <User className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="text-sm truncate">{adminUser?.full_name || adminUser?.email || "Admin"}</span>
                </div>
                <Button variant="outline" size="sm" onClick={logout} className="h-10 min-w-[44px]">
                  <LogOut className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Logout</span>
                </Button>
              </div>
            </div>
          </header>

          {/* Page Content */}
          <section className="flex-1 overflow-auto px-4 py-6 mt-2">
            <AdminNoticesBanner role={adminUser?.role} />
            {activeTab === "dashboard" && (
              <div className="space-y-6">
                <CustomDashboard userRole={adminUser?.role || "regional_admin"} userRegion={adminUser?.region_id} />

                <div className="grid gap-6 md:grid-cols-2">
                  <ActivityFeed
                    userRole={adminUser?.role || "regional_coordinator"}
                    userRegion={adminUser?.region_id}
                  />

                  <Card>
                    <CardHeader>
                      <CardTitle>Quick Actions</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {adminUser?.role === "kit_admin" ? (
                        <>
                          <Button
                            variant="outline"
                            className="w-full justify-start"
                            onClick={() => setActiveTab("kit")}
                          >
                            <Heart className="mr-2 h-4 w-4" />
                            Manage Testimonies
                          </Button>
                          <Button
                            variant="outline"
                            className="w-full justify-start"
                            onClick={() => setActiveTab("kit")}
                          >
                            <BookOpen className="mr-2 h-4 w-4" />
                            Manage Articles
                          </Button>
                          <Button
                            variant="outline"
                            className="w-full justify-start"
                            onClick={() => setActiveTab("kit")}
                          >
                            <BookOpen className="mr-2 h-4 w-4" />
                            Manage Magazines
                          </Button>
                        </>
                      ) : adminUser?.role === "regional_admin" ? (
                        <>
                          <Button
                            variant="outline"
                            className="w-full justify-start"
                            onClick={() => setActiveTab("regions")}
                          >
                            <MapPin className="mr-2 h-4 w-4" />
                            View Regions
                          </Button>
                          <Button
                            variant="outline"
                            className="w-full justify-start"
                            onClick={() => setActiveTab("messages")}
                          >
                            <Mail className="mr-2 h-4 w-4" />
                            Manage Messages
                          </Button>
                          <Button
                            variant="outline"
                            className="w-full justify-start"
                            onClick={() => setActiveTab("prayers")}
                          >
                            <Heart className="mr-2 h-4 w-4" />
                            Prayer Requests
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            variant="outline"
                            className="w-full justify-start"
                            onClick={() => setActiveTab("members")}
                          >
                            <Users className="mr-2 h-4 w-4" />
                            Manage Members
                          </Button>
                          <Button
                            variant="outline"
                            className="w-full justify-start"
                            onClick={() => setActiveTab("activities")}
                          >
                            <Activity className="mr-2 h-4 w-4" />
                            Manage Activities
                          </Button>
                          <Button
                            variant="outline"
                            className="w-full justify-start"
                            onClick={() => setActiveTab("regions")}
                          >
                            <MapPin className="mr-2 h-4 w-4" />
                            View Regions
                          </Button>
                        </>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {adminUser?.role === "global_admin" && <PerfMetricsPanel />}
              </div>
            )}


            {activeTab === "members" && userVisibleTabs.includes("members") && (
              <MemberManager userRole={adminUser?.role || ""} userRegion={adminUser?.region_id} />
            )}

            {activeTab === "activities" && userVisibleTabs.includes("activities") && (
              <ActivityManager userRole={adminUser?.role || ""} userRegion={adminUser?.region_id} />
            )}

            {activeTab === "regions" && userVisibleTabs.includes("regions") && (
              <EnhancedRegionalDashboard userRegion={adminUser?.region_id || ""} isGlobalAdmin={isGlobalAdmin} />
            )}

            {activeTab === "kit" && userVisibleTabs.includes("kit") && (
              <KitDashboard userRegion={adminUser?.region_id || ""} isGlobalAdmin={isGlobalAdmin} />
            )}

            {activeTab === "gallery" && userVisibleTabs.includes("gallery") && <GalleryManager />}

            {activeTab === "gallery-settings" && userVisibleTabs.includes("gallery-settings") && <GallerySettingsManager />}

            {activeTab === "messages" && userVisibleTabs.includes("messages") && (
              <MessagesManager userRole={adminUser?.role || ""} userRegion={adminUser?.region_id} />
            )}

            {activeTab === "chats" && userVisibleTabs.includes("chats") && <ChatSessionsManager />}

            {activeTab === "prayers" && userVisibleTabs.includes("prayers") && (
              <PrayerRequestsManager
                userRole={adminUser?.role || ""}
                userRegion={adminUser?.region_id}
                isGlobalAdmin={isGlobalAdmin}
              />
            )}

            {activeTab === "prayer-letter" && userVisibleTabs.includes("prayer-letter") && (
              <PrayerLetterManager />
            )}



            {activeTab === "registrations" && userVisibleTabs.includes("registrations") && (
              <div className="space-y-6">
                <ConferenceRegistrationsManager
                  isAuthorized={
                    adminUser?.role === "global_admin" ||
                    adminUser?.role === "president" ||
                    adminUser?.role === "regional_admin"
                  }
                  userRole={adminUser?.role}
                  userRegionId={adminUser?.region_id || null}
                  selectedRegion={registrationRegion}
                  onSelectedRegionChange={setRegistrationRegion}
                />
                <PaymentConfirmationsPanel
                  isAuthorized={
                    adminUser?.role === "global_admin" ||
                    adminUser?.role === "president" ||
                    adminUser?.role === "regional_admin"
                  }
                  userRole={adminUser?.role}
                  userRegionId={adminUser?.region_id || null}
                  selectedRegion={registrationRegion}
                />
                <ScheduledReportsManager
                  userRole={adminUser?.role}
                  regionId={adminUser?.region_id || null}
                />
                <LiveSheetSyncPanel
                  userRole={adminUser?.role}
                  userRegionId={adminUser?.region_id || null}
                />
              </div>
            )}

            {activeTab === "conf-analytics" && userVisibleTabs.includes("conf-analytics") && (
              <ConferenceAnalyticsDashboard />
            )}

            {activeTab === "checkin" && userVisibleTabs.includes("checkin") && (
              <CheckinDashboard userRole={adminUser?.role} userRegionId={adminUser?.region_id || null} />
            )}


            {activeTab === "badges" && userVisibleTabs.includes("badges") && (
              <BadgeGenerator userRole={adminUser?.role || ""} userRegionId={adminUser?.region_id} />
            )}

            {activeTab === "cancellations" && userVisibleTabs.includes("cancellations") && (
              <CancellationRequestsManager />
            )}

            {activeTab === "conference-content" && userVisibleTabs.includes("conference-content") && (
              <ConferenceContentManager />
            )}

            {activeTab === "conference-kit" && userVisibleTabs.includes("conference-kit") && (
              <ConferenceKitDashboard
                userRegionId={adminUser?.region_id}
                userRole={adminUser?.role || ""}
                isGlobalAdmin={isGlobalAdmin}
              />
            )}

            {activeTab === "notices" && userVisibleTabs.includes("notices") && (
              <NoticesManager isGlobalAdmin={isGlobalAdmin} userName={adminUser?.full_name || adminUser?.email} />
            )}

            {activeTab === "audit" && adminUser?.role === "global_admin" && <AuditLogViewer />}

            {activeTab === "mcp-console" && userVisibleTabs.includes("mcp-console") && <MCPConsole />}

            {activeTab === "settings" && userVisibleTabs.includes("settings") && (
              <SettingsManager userRole={adminUser?.role || ""} userRegion={adminUser?.region_id} />
            )}
          </section>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default AdminDashboard;
